import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import PWAInstallPrompt from '../../components/common/PWAInstallPrompt';
import EmptyState from '../../components/common/EmptyState';
import Badge from '../../components/common/Badge';
import {
  HiOutlineUsers, HiOutlineLocationMarker, HiOutlineClipboardCheck,
  HiOutlineCurrencyDollar, HiOutlineCash, HiOutlineArrowRight,
} from 'react-icons/hi';

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try { const { data } = await API.get('/admin/dashboard'); setData(data); }
    catch (error) { console.error('Dashboard error:', error); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
          <p className="text-sm text-ink-500 dark:text-ink-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const fmt = (n) => `৳${Number(n || 0).toLocaleString('en-IN')}`;

  const stats = [
    {
      title: t('totalWorkers'), value: data.stats.totalWorkers,
      icon: HiOutlineUsers, gradient: 'from-sky-500 to-blue-600',
    },
    {
      title: t('activeToday'), value: data.thisWeek.activeToday,
      icon: HiOutlineClipboardCheck, gradient: 'from-brand-500 to-emerald-600',
      dot: true,
    },
    {
      title: t('totalSites'), value: data.stats.totalSites,
      icon: HiOutlineLocationMarker, gradient: 'from-violet-500 to-purple-600',
    },
    {
      title: t('pendingPayments'), value: fmt(data.thisWeek.totalPendingAmount),
      icon: HiOutlineCurrencyDollar, gradient: 'from-accent-500 to-orange-600',
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('dashboard')}
        subtitle={`${t('thisWeek')}: ${data.thisWeek.weekStart} → ${data.thisWeek.weekEnd}`}
      />

      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((stat) => (
          <div key={stat.title} className="stat-card-hover group">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center text-white shadow-pop`}>
                <stat.icon className="w-6 h-6" />
              </div>
              {stat.dot && <Badge variant="success" dot>Live</Badge>}
            </div>
            <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider font-medium">{stat.title}</p>
            <p className="text-2xl font-bold text-ink-900 dark:text-white mt-1">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Pending payments + advance requests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              {t('pendingPayments')}
            </h2>
            <Link to="/admin/salary" className="text-xs text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1">
              সব দেখুন <HiOutlineArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {data.thisWeek.pendingPayments.length === 0 ? (
            <EmptyState icon="✅" title="কোনো বকেয়া নেই" description="সব বেতন পরিশোধিত!" />
          ) : (
            <div className="space-y-2">
              {data.thisWeek.pendingPayments.map((salary) => (
                <div key={salary.id} className="flex items-center justify-between p-3 bg-accent-50 dark:bg-accent-900/20 hover:bg-accent-100 dark:hover:bg-accent-900/30 rounded-xl transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-accent-200 dark:bg-accent-800/60 flex items-center justify-center text-accent-700 dark:text-accent-200 font-bold text-sm flex-shrink-0">
                      {salary.worker.name.charAt(0)}
                    </div>
                    <span className="font-medium text-ink-900 dark:text-white truncate">{salary.worker.name}</span>
                  </div>
                  <span className="text-accent-700 dark:text-accent-300 font-bold whitespace-nowrap">{fmt(salary.net_amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
              <span className="w-1.5 h-6 rounded-full bg-violet-500" />
              {t('pendingRequests')}
            </h2>
            <Link to="/admin/advances" className="text-xs text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1">
              সব দেখুন <HiOutlineArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {data.stats.pendingAdvances.length === 0 ? (
            <EmptyState icon="🎉" title="কোনো অনুরোধ নেই" description="সব অগ্রিম অনুমোদিত!" />
          ) : (
            <div className="space-y-2">
              {data.stats.pendingAdvances.map((advance) => (
                <div key={advance.id} className="flex items-center justify-between p-3 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/30 rounded-xl transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-violet-200 dark:bg-violet-800/60 flex items-center justify-center text-violet-700 dark:text-violet-200 font-bold text-sm flex-shrink-0">
                      {advance.worker.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-ink-900 dark:text-white truncate block">{advance.worker.name}</span>
                      {advance.reason && <span className="text-xs text-ink-500 dark:text-ink-400 truncate block">{advance.reason}</span>}
                    </div>
                  </div>
                  <span className="text-violet-700 dark:text-violet-300 font-bold whitespace-nowrap">{fmt(advance.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Last month summary */}
      <div className="card">
        <h2 className="text-base font-semibold text-ink-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="w-1.5 h-6 rounded-full bg-brand-500" />
          {t('lastMonth')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: t('totalPaid'),    value: data.lastMonth.totalPaid,    icon: '💰', bg: 'bg-brand-50 dark:bg-brand-900/20', color: 'text-brand-700 dark:text-brand-300' },
            { label: t('pending'),      value: data.lastMonth.totalPending, icon: '⏳', bg: 'bg-accent-50 dark:bg-accent-900/20', color: 'text-accent-700 dark:text-accent-300' },
            { label: t('totalAdvance'), value: data.lastMonth.totalAdvance, icon: '💸', bg: 'bg-violet-50 dark:bg-violet-900/20', color: 'text-violet-700 dark:text-violet-300' },
          ].map((s) => (
            <div key={s.label} className={`p-4 rounded-2xl ${s.bg} text-center`}>
              <div className="text-3xl mb-2">{s.icon}</div>
              <p className="text-xs uppercase tracking-wider font-medium text-ink-500 dark:text-ink-400">{s.label}</p>
              <p className={`text-2xl font-bold mt-1 ${s.color}`}>{fmt(s.value)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  );
};

export default AdminDashboard;
